<script>
    import { createEventDispatcher, onMount, tick } from 'svelte';
    import { fly } from 'svelte/transition';

    const dispatch = createEventDispatcher();

    export let position = 'bottom';
    export let is_open = false;
    export let closeOnClickOutside = true;

    let trigger_element;
    let popover_element;
    let trigger_rect = { top: 0, left: 0, width: 0, height: 0 };
    let position_styles = '';
    const viewport_padding = 12;

    async function toggle_popover() {
        if (is_open) {
            close_popover();
            return;
        }

        is_open = true;
        await tick();
        update_position_styles();
        dispatch('open');
    }

    function close_popover() {
        is_open = false;
        dispatch('close');
    }

    function handle_click_outside(event) {
        if (
            closeOnClickOutside &&
            is_open &&
            trigger_element &&
            popover_element
        ) {
            if (
                !trigger_element.contains(event.target) &&
                !popover_element.contains(event.target)
            ) {
                close_popover();
            }
        }
    }

    function handle_keydown(event) {
        if (event.key === 'Escape' && is_open) {
            close_popover();
        }
    }

    function update_position_styles() {
        if (!trigger_element) return;

        const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
        trigger_rect = trigger_element.getBoundingClientRect();
        const popover_rect = popover_element?.getBoundingClientRect();
        const popover_width = popover_rect?.width || 0;
        const popover_height = popover_rect?.height || 0;
        const styles = {};

        switch (position) {
            case 'top':
                styles.bottom = `${window.innerHeight - trigger_rect.top}px`;
                styles.left = `${clamp(
                    trigger_rect.left + trigger_rect.width / 2,
                    viewport_padding + popover_width / 2,
                    window.innerWidth - viewport_padding - popover_width / 2
                )}px`;
                break;
            case 'bottom':
                styles.top = `${trigger_rect.bottom}px`;
                styles.left = `${clamp(
                    trigger_rect.left + trigger_rect.width / 2,
                    viewport_padding + popover_width / 2,
                    window.innerWidth - viewport_padding - popover_width / 2
                )}px`;
                break;
            case 'left':
                styles.top = `${clamp(
                    trigger_rect.top + trigger_rect.height / 2,
                    viewport_padding + popover_height / 2,
                    window.innerHeight - viewport_padding - popover_height / 2
                )}px`;
                styles.right = `${window.innerWidth - trigger_rect.left}px`;
                break;
            case 'right':
                styles.top = `${clamp(
                    trigger_rect.top + trigger_rect.height / 2,
                    viewport_padding + popover_height / 2,
                    window.innerHeight - viewport_padding - popover_height / 2
                )}px`;
                styles.left = `${trigger_rect.right}px`;
                break;
        }

        position_styles = Object.entries(styles)
            .map(([key, value]) => `${key}: ${value}`)
            .join('; ');
    }

    $: if (is_open) {
        tick().then(update_position_styles);
    }

    onMount(() => {
        document.addEventListener('click', handle_click_outside);
        document.addEventListener('keydown', handle_keydown);

        document.addEventListener('scroll', update_position_styles, {
            passive: true
        });
        window.addEventListener('resize', update_position_styles);

        return () => {
            document.removeEventListener('click', handle_click_outside);
            document.removeEventListener('keydown', handle_keydown);
            document.removeEventListener('scroll', update_position_styles);
            window.removeEventListener('resize', update_position_styles);
        };
    });
</script>

<div class="inline-block relative" bind:this={trigger_element}>
    <div
        on:click={toggle_popover}
        role="button"
        tabindex="0"
        aria-expanded={is_open}
        aria-haspopup="dialog"
    >
        <slot />
    </div>

    {#if is_open}
        <div
            bind:this={popover_element}
            class="fixed z-50 bg-black/90 border border-border-default rounded-lg text-symbols-default shadow-lg p-3 max-w-xs break-words focus:outline focus:outline-2 focus:outline-border-highlight focus:outline-offset-2 backdrop-blur
                {position === 'top'
                ? '-translate-x-1/2 -translate-y-2'
                : position === 'bottom'
                ? '-translate-x-1/2 translate-y-2'
                : position === 'left'
                ? '-translate-y-1/2 -translate-x-2'
                : position === 'right'
                ? 'translate-y-1/2 translate-x-2'
                : ''}"
            style={position_styles}
            role="dialog"
            aria-modal="true"
            transition:fly={{
                duration: 200,
                y: position === 'top' ? 10 : position === 'bottom' ? -10 : 0,
                x: position === 'left' ? 10 : position === 'right' ? -10 : 0
            }}
        >
            <slot name="content" />
        </div>
    {/if}
</div>
